import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { Enrollment, EnrollTeamRequest } from '@sulis-liiga/common';
import enrollmentService from '../../services/enrollmentService';

interface EnrollmentState {
  teamEnrollments: Enrollment[];
  seasonEnrollments: Enrollment[];
  loading: boolean;
  error: string | null;
  enrollSuccess: boolean;
}

const initialState: EnrollmentState = {
  teamEnrollments: [],
  seasonEnrollments: [],
  loading: false,
  error: null,
  enrollSuccess: false,
};

// ── Async thunks ──────────────────────────────────────────────────────────────

export const enrollTeam = createAsyncThunk(
  'enrollment/enrollTeam',
  async ({ teamId, req }: { teamId: string; req: EnrollTeamRequest }, { rejectWithValue }) => {
    try {
      return await enrollmentService.enrollTeam(teamId, req);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to enroll team. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const fetchTeamEnrollments = createAsyncThunk(
  'enrollment/fetchTeamEnrollments',
  async (teamId: string, { rejectWithValue }) => {
    try {
      return await enrollmentService.getTeamEnrollments(teamId);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to fetch enrollments. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const withdrawEnrollment = createAsyncThunk(
  'enrollment/withdrawEnrollment',
  async (enrollmentId: string, { rejectWithValue }) => {
    try {
      await enrollmentService.withdrawEnrollment(enrollmentId);
      return enrollmentId;
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to withdraw enrollment. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const placeEnrollment = createAsyncThunk(
  'enrollment/placeEnrollment',
  async ({ id, divisionId }: { id: string; divisionId: string }, { rejectWithValue }) => {
    try {
      return await enrollmentService.placeEnrollment(id, { divisionId });
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to place enrollment. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const fetchSeasonEnrollments = createAsyncThunk(
  'enrollment/fetchSeasonEnrollments',
  async (
    { leagueId, seasonId, status }: { leagueId: string; seasonId: string; status?: string },
    { rejectWithValue },
  ) => {
    try {
      return await enrollmentService.getSeasonEnrollments(leagueId, seasonId, status);
    } catch (error) {
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to fetch season enrollments. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const enrollmentSlice = createSlice({
  name: 'enrollment',
  initialState,
  reducers: {
    clearEnrollmentError: state => {
      state.error = null;
    },
    clearEnrollSuccess: state => {
      state.enrollSuccess = false;
    },
  },
  extraReducers: builder => {
    // enrollTeam
    builder.addCase(enrollTeam.pending, state => {
      state.loading = true;
      state.error = null;
      state.enrollSuccess = false;
    });
    builder.addCase(enrollTeam.fulfilled, (state, action) => {
      state.loading = false;
      state.enrollSuccess = true;
      state.teamEnrollments.push(action.payload);
    });
    builder.addCase(enrollTeam.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.enrollSuccess = false;
    });

    // fetchTeamEnrollments
    builder.addCase(fetchTeamEnrollments.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTeamEnrollments.fulfilled, (state, action) => {
      state.loading = false;
      state.teamEnrollments = action.payload ?? [];
    });
    builder.addCase(fetchTeamEnrollments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // withdrawEnrollment
    builder.addCase(withdrawEnrollment.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(withdrawEnrollment.fulfilled, (state, action) => {
      state.loading = false;
      const id = action.payload;
      state.teamEnrollments = state.teamEnrollments.filter(e => e.id !== id);
      state.seasonEnrollments = state.seasonEnrollments.filter(e => e.id !== id);
    });
    builder.addCase(withdrawEnrollment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // placeEnrollment
    builder.addCase(placeEnrollment.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(placeEnrollment.fulfilled, (state, action) => {
      state.loading = false;
      const update = (list: Enrollment[]) =>
        list.map(e => (e.id === action.payload.id ? action.payload : e));
      state.seasonEnrollments = update(state.seasonEnrollments);
      state.teamEnrollments = update(state.teamEnrollments);
    });
    builder.addCase(placeEnrollment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchSeasonEnrollments
    builder.addCase(fetchSeasonEnrollments.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSeasonEnrollments.fulfilled, (state, action) => {
      state.loading = false;
      state.seasonEnrollments = action.payload ?? [];
    });
    builder.addCase(fetchSeasonEnrollments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearEnrollmentError, clearEnrollSuccess } = enrollmentSlice.actions;
export default enrollmentSlice.reducer;
